import { apiRequest } from "./client";
import {
  getHrEmployeeSession,
  getHrEmployerSession,
} from "@/lib/auth/storage";

function employeeToken() {
  return getHrEmployeeSession()?.token ?? null;
}

function employerToken() {
  return getHrEmployerSession()?.token ?? null;
}

export const hrEmployeeApi = {
  login(identifier: string, password: string) {
    return apiRequest<{ token: string; role: string; employee: any }>(
      "/hr/employee/login",
      { method: "POST", body: { identifier, password } }
    );
  },
  dashboard() {
    return apiRequest<any>("/hr/employee/dashboard", { token: employeeToken() });
  },
  profile() {
    return apiRequest<any>("/hr/employee/profile", { token: employeeToken() });
  },
  attendanceHistory(month: string) {
    return apiRequest<any>("/hr/employee/attendance/history", {
      token: employeeToken(),
      params: { month },
    });
  },
  leave() {
    return apiRequest<any>("/hr/employee/leave", { token: employeeToken() });
  },
  requestLeave(body: {
    leave_type_id: number;
    start_date: string;
    end_date?: string;
    reason?: string;
  }) {
    return apiRequest<any>("/hr/employee/leave", {
      method: "POST",
      token: employeeToken(),
      body,
    });
  },
  cancelLeave(id: number) {
    return apiRequest<any>(`/hr/employee/leave/${id}/cancel`, {
      method: "POST",
      token: employeeToken(),
    });
  },
  hours() {
    return apiRequest<any>("/hr/employee/hours", { token: employeeToken() });
  },
  logHours(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employee/hours", {
      method: "POST",
      token: employeeToken(),
      body,
    });
  },
  submitTimesheet(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employee/timesheets", {
      method: "POST",
      token: employeeToken(),
      body,
    });
  },
  tasks(day?: string) {
    return apiRequest<any>("/hr/employee/tasks", {
      token: employeeToken(),
      params: day ? { day } : undefined,
    });
  },
  completeTask(id: number, notes?: string) {
    return apiRequest<any>(`/hr/employee/tasks/${id}/complete`, {
      method: "POST",
      token: employeeToken(),
      body: { notes },
    });
  },
  additionalWork(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employee/additional-work", {
      method: "POST",
      token: employeeToken(),
      body,
    });
  },
  submitReport(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employee/work-report", {
      method: "POST",
      token: employeeToken(),
      body,
    });
  },
  performance() {
    return apiRequest<any>("/hr/employee/performance", { token: employeeToken() });
  },
  awards() {
    return apiRequest<any>("/hr/employee/awards", { token: employeeToken() });
  },
  notifications() {
    return apiRequest<any>("/hr/employee/notifications", { token: employeeToken() });
  },
  markRead(id: number) {
    return apiRequest<any>(`/hr/employee/notifications/${id}/read`, {
      method: "POST",
      token: employeeToken(),
    });
  },
};

export const hrEmployerApi = {
  login(email: string, password: string) {
    return apiRequest<{ token: string; role: string; employer: any }>(
      "/hr/employer/login",
      { method: "POST", body: { email, password } }
    );
  },
  dashboard() {
    return apiRequest<any>("/hr/employer/dashboard", { token: employerToken() });
  },
  employees(params?: Record<string, string | undefined>) {
    return apiRequest<any[]>("/hr/employer/employees", {
      token: employerToken(),
      params,
    });
  },
  createEmployee(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/employees", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  getEmployee(id: number) {
    return apiRequest<any>(`/hr/employer/employees/${id}`, { token: employerToken() });
  },
  updateEmployee(id: number, body: Record<string, unknown>) {
    return apiRequest<any>(`/hr/employer/employees/${id}`, {
      method: "PUT",
      token: employerToken(),
      body,
    });
  },
  employeeDashboard(id: number) {
    return apiRequest<any>(`/hr/employer/employees/${id}/dashboard`, {
      token: employerToken(),
    });
  },
  linkPin(id: number, device_pin: string) {
    return apiRequest<any>(`/hr/employer/employees/${id}/link-pin`, {
      method: "POST",
      token: employerToken(),
      body: { device_pin },
    });
  },
  departments() {
    return apiRequest<any[]>("/hr/employer/departments", { token: employerToken() });
  },
  upsertDepartment(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/departments", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  leaveTypes() {
    return apiRequest<any[]>("/hr/employer/leave-types", { token: employerToken() });
  },
  upsertLeaveType(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/leave-types", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  leavePolicies() {
    return apiRequest<any[]>("/hr/employer/leave-policies", { token: employerToken() });
  },
  upsertPolicy(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/leave-policies", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  leaveRequests(status?: string) {
    return apiRequest<any[]>("/hr/employer/leave-requests", {
      token: employerToken(),
      params: status ? { status } : undefined,
    });
  },
  decideLeave(id: number, decision: string, comment?: string) {
    return apiRequest<any>(`/hr/employer/leave-requests/${id}/decide`, {
      method: "POST",
      token: employerToken(),
      body: { decision, comment },
    });
  },
  adjustLeave(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/leave-adjustments", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  hours(employeeId?: number) {
    return apiRequest<any>("/hr/employer/hours", {
      token: employerToken(),
      params: employeeId ? { employee_id: String(employeeId) } : undefined,
    });
  },
  taskDefinitions() {
    return apiRequest<any[]>("/hr/employer/task-definitions", { token: employerToken() });
  },
  upsertTaskDefinition(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/task-definitions", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  taskAssignments(params?: Record<string, string | undefined>) {
    return apiRequest<any[]>("/hr/employer/task-assignments", {
      token: employerToken(),
      params,
    });
  },
  upsertAssignment(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/task-assignments", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  verifyAssignment(id: number) {
    return apiRequest<any>(`/hr/employer/task-assignments/${id}/verify`, {
      method: "POST",
      token: employerToken(),
    });
  },
  workReports() {
    return apiRequest<any[]>("/hr/employer/work-reports", { token: employerToken() });
  },
  reviewReport(id: number, status: string, comment?: string) {
    return apiRequest<any>(`/hr/employer/work-reports/${id}/review`, {
      method: "POST",
      token: employerToken(),
      body: { status, comment },
    });
  },
  awards() {
    return apiRequest<any[]>("/hr/employer/awards", { token: employerToken() });
  },
  giveAward(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/awards", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  revokeAward(id: number) {
    return apiRequest<any>(`/hr/employer/awards/${id}/revoke`, {
      method: "POST",
      token: employerToken(),
    });
  },
  awardTypes() {
    return apiRequest<any[]>("/hr/employer/award-types", { token: employerToken() });
  },
  rules() {
    return apiRequest<any[]>("/hr/employer/performance-rules", { token: employerToken() });
  },
  upsertRule(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/performance-rules", {
      method: "POST",
      token: employerToken(),
      body,
    });
  },
  settings() {
    return apiRequest<any>("/hr/employer/settings", { token: employerToken() });
  },
  saveSettings(body: Record<string, unknown>) {
    return apiRequest<any>("/hr/employer/settings", {
      method: "PUT",
      token: employerToken(),
      body,
    });
  },
  reports(kind: string) {
    return apiRequest<any[]>(`/hr/employer/reports/${kind}`, { token: employerToken() });
  },
  calendar(month: string) {
    return apiRequest<any>("/hr/employer/calendar", {
      token: employerToken(),
      params: { month },
    });
  },
  audit(employeeId?: number) {
    return apiRequest<any[]>("/hr/employer/audit", {
      token: employerToken(),
      params: employeeId ? { employee_id: String(employeeId) } : undefined,
    });
  },
};
